var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
// Mimic RNA-FISH's EditKitForm, as we have the same problems to solve here.
// Should be inside a `Form` whose `error` prop is set appropriately.
import * as React from "react";
import { Form, Grid, Header, Table } from "semantic-ui-react";
import ErrorDisplay from "../../ErrorDisplay";
import { Help } from "../../Help";
import { postJsonWithAuthn } from "../../RequestHelpers";
import { buildOnComponentStateChangeHandler } from "../FormState";
import { encoderAmplifierCatalogFor } from "../secondary-if/SecondaryIfCatalog";
import { defaultAmpState, defaultSampleTypeState, discountMessage, priceColumn, PriceDisplay, ToggleableKitComponent } from "../rna-fish/EditKitForm";
import { HcrAmplifierForm } from "../rna-fish/HcrAmplifierForm";
import KitSerializer, { serializePriceMode } from "../rna-fish/KitSerializer";
import { ErrorElementsWithPrices } from "../rna-fish/NewRnaFishKitPage";
import SelectSampleTypeForm from "../rna-fish/SelectSampleTypeForm";
import BufferForm from "./BufferForm";
import EncoderComponentForm from "./EncoderComponentForm";
export var defaultEncoderComponentState = function (catalog) { return ({
    scale: catalog.encoder.defaultScale,
    // No species selected yet — fall back to the first encoder's default amplifier.
    hcrAmplifier: encoderAmplifierCatalogFor(catalog).defaultChannel.hcrAmplifier
}); };
export var defaultBuffersComponentState = function (catalog) { return ({
    antibody: catalog.encoderBuffer.antibody.defaultVolumeMl,
    antibodyWash: catalog.encoderBuffer.antibodyWash.defaultVolumeMl,
    amplification: catalog.encoderBuffer.amplification.defaultVolumeMl,
    goldAmpWash: catalog.encoderBuffer.goldAmpWash.defaultVolumeMl
}); };
var EditKitForm = /** @class */ (function (_super) {
    __extends(EditKitForm, _super);
    function EditKitForm(props) {
        var _this = _super.call(this, props) || this;
        _this.onEncoderComponentStateChange = buildOnComponentStateChangeHandler(encoderComponentStateWrangler, _this.props.setFormState);
        _this.onBufferStateChange = buildOnComponentStateChangeHandler(bufStateWrangler, _this.props.setFormState);
        _this.onSampleTypeStateChangeBaseHandler = buildOnComponentStateChangeHandler(sampleTypeStateWrangler, _this.props.setFormState);
        _this.ampLabel = React.createElement(React.Fragment, null,
            "Amplifier ",
            React.createElement(Help, null,
                React.createElement("p", null, "For multiplex experiments, choose a different HCR\u2122 Gold Amplifier (X1, X2, ...) for each target that will be imaged in the same sample (for example, amplifier X1 for target 1, amplifier X2 for target 2, ...). Note that if your kit includes an HCR\u2122 HiFi Encoder, this selection also sets the Encoder\u2019s \u201CFor use with amplifier\u201D above."),
                React.createElement("p", null, "For flexibility, 488, 546, 594, and 647 are available across all X1-X10 systems. For expanded channel options, select amplifiers also offer an additional unique label option. We recommend using a longer-wavelength label (e.g., 647 or 750) for targets that are more difficult to detect (e.g., low-expression targets), as autofluorescence tends to be higher in shorter-wavelength channels (e.g., 405 or 488).")));
        _this.experimentVolumeTable = React.createElement(Table, { celled: true, striped: true },
            React.createElement(Table.Header, null,
                React.createElement(Table.Row, null,
                    React.createElement(Table.HeaderCell, null, "Sample type"),
                    React.createElement(Table.HeaderCell, null, "Reaction volume (\u00B5L)"),
                    React.createElement(Table.HeaderCell, null, "HCR\u2122 Gold Amplifier (\u00B5L)"),
                    React.createElement(Table.HeaderCell, null, "Experiments"))),
            React.createElement(Table.Body, null,
                React.createElement(Table.Row, null,
                    React.createElement(Table.Cell, null, "Sample on a slide (e.g., tissue section or adherent cells)"),
                    React.createElement(Table.Cell, null, "100"),
                    React.createElement(Table.Cell, null, "2"),
                    React.createElement(Table.Cell, null, "100")),
                React.createElement(Table.Row, null,
                    React.createElement(Table.Cell, null, "Sample in solution (e.g., whole-mount embryo or cells in suspension)"),
                    React.createElement(Table.Cell, null, "200"),
                    React.createElement(Table.Cell, null, "4"),
                    React.createElement(Table.Cell, null, "50"))));
        _this.onSampleTypeStateChange = function (sampleTypeFormState, _, callback) {
            return _this.onSampleTypeStateChangeBaseHandler(sampleTypeFormState, callback);
        };
        _this.onEncoderComponentEnableChange = function () {
            return _this.props.setFormState(function (s) {
                var enabling = !s.encoderComponentEnabled;
                if (!enabling) {
                    return { encoderComponentEnabled: false };
                }
                // Re-enabling: the encoder governs the amplifier again. If the current amp (e.g. one picked
                // while the encoder was disabled and the full set was shown) isn't valid for the encoder's
                // species, reset amp/label/scale to the species' default. One atomic update so render never
                // sees a species/amp mismatch (which would throw).
                var ampCatalog = encoderAmplifierCatalogFor(_this.props.catalog, s.encoderComponentState.primarySpecies);
                var stillValid = ampCatalog.channels.some(function (a) {
                    return (s.ampState.adminSystems || !a.admin) && a.hcrAmplifier.id === s.ampState.hcrAmplifier.id;
                });
                if (stillValid) {
                    return { encoderComponentEnabled: true };
                }
                var channel = ampCatalog.defaultChannel;
                return {
                    encoderComponentEnabled: true,
                    encoderComponentState: Object.assign({}, s.encoderComponentState, { hcrAmplifier: channel.hcrAmplifier }),
                    ampState: Object.assign({}, s.ampState, {
                        hcrAmplifier: channel.hcrAmplifier,
                        label: channel.defaultLabel.label
                    })
                };
            }, _this.onUserSelection);
        };
        _this.onAmplifierComponentEnableChange = function () {
            return _this.props.setFormState(function (s) { return ({ ampEnabled: !s.ampEnabled }); }, _this.onUserSelection);
        };
        _this.onBuffersComponentEnableChange = function () {
            return _this.props.setFormState(function (s) { return ({ buffersEnabled: !s.buffersEnabled }); }, _this.onUserSelection);
        };
        _this.onCommentChange = function (event) {
            var str = event.currentTarget.value;
            _this.props.setFormState(function () {
                return {
                    comment: str
                };
            });
        };
        _this.onEncoderStateChange = function (updateObjOrFn, prevAmplifier, callback) {
            var stateCalculator;
            if (typeof updateObjOrFn === "function") {
                stateCalculator = function (oldState) {
                    // apply the provided function
                    return updateObjOrFn(oldState.encoderComponentState);
                };
            }
            else {
                stateCalculator = function () {
                    // just return the plain object
                    return updateObjOrFn;
                };
            }
            // Update the encoder component AND its dependent amplifier state in a single setFormState so
            // there is never an intermediate render where the (possibly new) species and the amplifier
            // disagree (e.g. switching to a species that disallows the current amp) — otherwise
            // HcrAmplifierForm renders against a catalog that doesn't contain ampState.hcrAmplifier.
            _this.props.setFormState(function (oldState) {
                var _a, _b;
                var newEncoderState = Object.assign({}, oldState.encoderComponentState, stateCalculator(oldState));
                var ampCatalog = encoderAmplifierCatalogFor(_this.props.catalog, newEncoderState.primarySpecies);
                var channel = (_a = ampCatalog.channels.find(function (a) {
                    return a.hcrAmplifier.id === newEncoderState.hcrAmplifier.id;
                })) !== null && _a !== void 0 ? _a : ampCatalog.defaultChannel;
                var label = oldState.ampState.label;
                if (newEncoderState.hcrAmplifier.id !== prevAmplifier.id) {
                    label = channel.defaultLabel.label;
                }
                // keep the current scale if it's still valid for the chosen label, else use the default
                var scale = (_b = channel.defaultLabel) === null || _b === void 0 ? void 0 : _b.scales.find(function (s) { return s.valuePmol === oldState.ampState.scale.valuePmol; });
                return {
                    encoderComponentState: newEncoderState,
                    ampState: Object.assign({}, oldState.ampState, {
                        hcrAmplifier: newEncoderState.hcrAmplifier,
                        label: label,
                        scale: scale ? scale : channel.defaultLabel.defaultScale
                    })
                };
            }, callback);
        };
        _this.onAmpStateChange = function (updateObjOrFn, callback) {
            var stateCalculator;
            if (typeof updateObjOrFn === "function") {
                stateCalculator = function (oldState) {
                    // apply the provided function
                    return updateObjOrFn(oldState.ampState);
                };
            }
            else {
                stateCalculator = function () {
                    // just return the plain object
                    return updateObjOrFn;
                };
            }
            _this.props.setFormState(function (oldState) { return ({
                ampState: Object.assign({}, oldState.ampState, stateCalculator(oldState))
            }); }, function (newState) {
                // propagate to probe set component
                _this.onEncoderComponentStateChange({
                    hcrAmplifier: newState.ampState.hcrAmplifier
                }, callback);
            });
        };
        // The user finished editing something
        _this.onUserSelection = function () {
            _this.loadPrice();
        };
        _this.loadPrice = function () {
            _this.props.setFormState(function () { return ({
                priceLoading: true
            }); }, function (newState) {
                postJsonWithAuthn(_this.props.kitPriceUrlPath, JSON.stringify(serializeManualGoldIfKit(newState)))
                    .then(function (resp) {
                    if (resp.status === 400) {
                        return resp.json().then(function (json) { return new ErrorElementsWithPrices(json.errors, json.price); });
                    }
                    return resp.json();
                }).then(function (resp) {
                    if (resp instanceof ErrorElementsWithPrices) {
                        _this.props.setFormState(function () { return ({
                            errors: resp.errors,
                            priceLoading: false,
                            price: resp.price
                        }); });
                    }
                    else {
                        _this.props.setFormState(function () { return ({
                            errors: [],
                            price: resp,
                            priceLoading: false
                        }); });
                    }
                }).catch(function (rej) {
                    console.error("Couldn't update price: " + rej);
                });
            });
        };
        return _this;
    }
    EditKitForm.prototype.render = function () {
        return React.createElement("div", { className: 'kit-form' },
            React.createElement(ErrorDisplay, { errors: this.props.formState.errors, errorPath: "/" }),
            React.createElement("div", null),
            React.createElement(Header, { className: "component-slider" },
                React.createElement("span", { className: 'sample-type-span' }, "Sample Type")),
            React.createElement("div", { className: 'no-outline-kit-component' },
                React.createElement(SelectSampleTypeForm, { sampleTypeState: this.props.formState.sampleTypeFormState, sampleTypes: this.props.catalog.encoderBuffer.sampleTypes, manualPro: false, onSampleTypeChange: this.onSampleTypeStateChange, onUserSelection: this.onUserSelection, errors: this.props.formState.errors, showErrors: this.props.showErrors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.encoderComponentEnabled, label: 'HCR™ HiFi Encoder', otherComponentStates: [this.props.formState.ampEnabled, this.props.formState.buffersEnabled], onChange: this.onEncoderComponentEnableChange, cish: false },
                React.createElement(EncoderComponentForm, { catalog: this.props.catalog, errors: this.props.formState.errors, effectivePriceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.encoderPriceCents : undefined, listPriceCents: this.props.formState.price ? this.props.formState.price.listPrice.encoderPriceCents : undefined, priceLoading: this.props.formState.priceLoading, encoderState: this.props.formState.encoderComponentState, onEncoderChange: this.onEncoderStateChange, onUserSelection: this.onUserSelection, selectedSampleType: this.props.formState.sampleTypeFormState.sampleType, showErrors: this.props.showErrors, enableCustomPrice: this.props.enableCustomPrice, adminSystems: this.props.formState.ampState.adminSystems, amplifierDropdownDisabled: this.props.formState.ampEnabled })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.ampEnabled, label: 'HCR™ Gold Amplifier', otherComponentStates: [this.props.formState.encoderComponentEnabled, this.props.formState.buffersEnabled], onChange: this.onAmplifierComponentEnableChange, cish: false },
                React.createElement(HcrAmplifierForm, { amplifierLabel: this.ampLabel, ampState: this.props.formState.ampState, 
                    // When the encoder is enabled the amplifier choice must stay within its species'
                    // amps (the encoder's dropdown mirrors this one); when disabled the amplifier is
                    // standalone, so show the full unrestricted set (no species filter).
                    catalog: this.props.formState.encoderComponentEnabled
                        ? encoderAmplifierCatalogFor(this.props.catalog, this.props.formState.encoderComponentState.primarySpecies)
                        : encoderAmplifierCatalogFor(this.props.catalog), errors: this.props.formState.errors, enableCustomPrice: this.props.enableCustomPrice, enableCustomScale: false, enableAdminSystems: this.props.enableAdminSystems, onAmpStateChange: this.onAmpStateChange, selectedSampleType: this.props.formState.sampleTypeFormState.sampleType, onUserSelection: this.onUserSelection, effectivePriceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.amplifierPriceCents : undefined, listPriceCents: this.props.formState.price ? this.props.formState.price.listPrice.amplifierPriceCents : undefined, priceLoading: this.props.formState.priceLoading, experimentVolumeTable: this.experimentVolumeTable, legacy: false, encoder: true, secondaryIf: false, showErrors: this.props.showErrors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.buffersEnabled, label: 'HCR™ Buffers', otherComponentStates: [this.props.formState.encoderComponentEnabled, this.props.formState.ampEnabled], onChange: this.onBuffersComponentEnableChange, cish: false },
                React.createElement(BufferForm, { catalog: this.props.catalog.encoderBuffer, errors: this.props.formState.errors, effectivePriceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.buffersPriceCents : undefined, listPriceCents: this.props.formState.price ? this.props.formState.price.listPrice.buffersPriceCents : undefined, priceLoading: this.props.formState.priceLoading, bufferState: this.props.formState.buffersState, onBufferChange: this.onBufferStateChange, onUserSelection: this.onUserSelection, showErrors: this.props.showErrors, enableCustomPrice: this.props.enableCustomPrice, enableCustomScale: false })),
            React.createElement(ErrorDisplay, { errors: this.props.formState.errors, errorPath: "/" }),
            React.createElement(Grid, { columns: 4, stackable: true },
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 12, className: "no-left-padding-mobile" },
                        React.createElement(Form.Field, null,
                            React.createElement("label", { htmlFor: 'comment' }, "Comment"),
                            React.createElement(Form.TextArea, { style: { fontFamily: 'Avenir Next' }, placeholder: 'Please describe any instructions for order fulfillment or ' +
                                    'anything else the MI Team should be aware of regarding this order.', value: this.props.formState.comment, onChange: this.onCommentChange }))),
                    priceColumn(React.createElement("div", { 
                        // apply padding that matches the kit component div padding
                        style: { 'paddingRight': '.5rem' } },
                        React.createElement(PriceDisplay, { loading: this.props.formState.priceLoading, priceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.subtotalCents : undefined })))),
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 16 }, this.props.formState.price && discountMessage(this.props.formState.price.discountCents, this.props.formState.price.listPrice.subtotalCents)))));
    };
    EditKitForm.prototype.componentDidMount = function () {
        if (this.props.loadPriceOnMount) {
            this.loadPrice();
        }
    };
    return EditKitForm;
}(React.PureComponent));
export { EditKitForm };
export var defaultManualGoldIfFormState = function (catalog) { return ({
    comment: '',
    errors: [],
    encoderComponentEnabled: true,
    encoderComponentState: defaultEncoderComponentState(catalog),
    ampEnabled: true,
    ampState: defaultAmpState(encoderAmplifierCatalogFor(catalog)),
    buffersEnabled: true,
    buffersState: defaultBuffersComponentState(catalog),
    sampleTypeFormState: defaultSampleTypeState(),
    priceLoading: false
}); };
// TODO tests for serialization
export var serializeManualGoldIfKit = function (state) {
    var kit = {
        comment: state.comment,
        starterKit: false,
        priceModes: {
            encoderMode: serializePriceMode(state.encoderComponentEnabled ? state.encoderComponentState.customPrice : undefined),
            amplifierMode: serializePriceMode(state.ampEnabled ? state.ampState.customPrice : undefined),
            buffersMode: serializePriceMode(state.buffersEnabled ? state.buffersState.customPrice : undefined)
        }
    };
    kit.sampleTypeId = state.sampleTypeFormState.sampleType.id;
    if (state.encoderComponentEnabled) {
        kit.encoderKit = serializeEncoderComponent(state.encoderComponentState);
    }
    if (state.ampEnabled) {
        kit.amplifierComponent = KitSerializer.serializeAmplifier(state.ampState);
    }
    if (state.buffersEnabled) {
        kit.buffersComponent = serializeBuffers(state.buffersState);
    }
    return {
        '_type': 'manual-gold-if',
        kit: kit
    };
};
export var serializeEncoderComponent = function (encoder) {
    var _a, _b;
    return {
        encodeA: (_b = (_a = encoder.primarySpecies) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '',
        hcrAmplifier: encoder.hcrAmplifier.id,
        scaleSlides: encoder.scale.slides
    };
};
export var serializeBuffers = function (buffers) { return ({
    antibodyBufferMl: buffers.antibody.valueMl,
    antibodyWashBufferMl: buffers.antibodyWash.valueMl,
    amplificationBufferMl: buffers.amplification.valueMl,
    goldAmpWashBufferMl: buffers.goldAmpWash.valueMl
}); };
var EncoderComponentStateWrangler = /** @class */ (function () {
    function EncoderComponentStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ encoderComponentState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.encoderComponentState; };
    }
    return EncoderComponentStateWrangler;
}());
var BufferStateWrangler = /** @class */ (function () {
    function BufferStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ buffersState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.buffersState; };
    }
    return BufferStateWrangler;
}());
var SampleTypeStateWrangler = /** @class */ (function () {
    function SampleTypeStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ sampleTypeFormState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.sampleTypeFormState; };
    }
    return SampleTypeStateWrangler;
}());
var encoderComponentStateWrangler = new EncoderComponentStateWrangler();
var bufStateWrangler = new BufferStateWrangler();
var sampleTypeStateWrangler = new SampleTypeStateWrangler();
//# sourceMappingURL=EditKitForm.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/kit/manual-gold-if/EditKitForm.js
// module id = 89
// module chunks = 0